import { A } from '@solidjs/router';
import { type JSX, Show } from 'solid-js';
import { t, tPlural } from '../i18n';
import { orgHref } from '../lib/org';
import { type RepoReviewRun, reviewVerdictKey } from '../lib/repo-reviews';
import { ReviewBrainBadge } from './ReviewBrainBadge';
import { ReviewStatusPill } from './ReviewStatusPill';
import { StatusPill } from './StatusPill';
import { buttonClass } from './ui/Button';

// One row of the repo Reviews tab. PRESENTER — every fact arrives as a prop; the
// read is `useRepoReviews` in `<RepoReviewsTab/>`.
//
// `findings_kept: null` IS NOT ZERO. It means the run never reached the findings
// step, and printing "0 findings posted" for it would report a clean review where
// there was no review — so the null has its own line. The same rule runs through
// `verdict`: null is "not posted yet", never "approved", and through `pr_number`:
// null is "this row does not know which pull request", and it renders as a LABEL
// with no link. The store used to hand `0` for that case and this row linked to
// `/pull/0` — a real GitHub URL, for a pull request that cannot exist.

export type RepoReviewItemProps = {
  readonly run: RepoReviewRun;
  /** `owner/repo`, decoded — the PR number is only addressable next to it. */
  readonly fullName: string;
};

/** The findings line: a count with its plural, or the honest "not recorded". */
function findingsLine(kept: number | null): string {
  if (kept === null) return t('repo_detail.reviews_findings_unknown');
  return tPlural('repo_detail.reviews_findings', kept, { count: String(kept) });
}

/** What the review told GitHub — the server's own string when this app has no copy for it. */
function verdictLine(verdict: string | null): string {
  if (verdict === null) return t('repo_detail.reviews_verdict_pending');
  const key = reviewVerdictKey(verdict);
  return key === null ? verdict : t(key);
}

export function RepoReviewItem(props: RepoReviewItemProps): JSX.Element {
  return (
    <li class="repo-reviews__item">
      <div class="repo-reviews__head">
        <Show
          when={props.run.pr_number}
          fallback={<span class="repo-reviews__pr">{t('repo_detail.reviews_pr_unknown')}</span>}
        >
          {(number) => (
            <a
              class="repo-reviews__pr"
              href={`https://github.com/${props.fullName}/pull/${number()}`}
              target="_blank"
              rel="noreferrer"
            >
              {t('repo_detail.reviews_pr', { number: String(number()) })}
            </a>
          )}
        </Show>
        <ReviewStatusPill status={props.run.status} delivery={props.run.delivery} />
        <Show when={props.run.incremental}>
          <StatusPill tone="neutral">{t('repo_detail.reviews_incremental')}</StatusPill>
        </Show>
        <Show when={props.run.head_sha}>
          {(sha) => (
            <code class="repo-reviews__sha">
              {t('repo_detail.reviews_head', { sha: sha().slice(0, 7) })}
            </code>
          )}
        </Show>
        <span class="repo-reviews__when">
          {t('repo_detail.reviews_when', { when: props.run.created_at.slice(0, 10) })}
        </span>
      </div>

      <div class="repo-reviews__outcome">
        <span class="repo-reviews__verdict">{verdictLine(props.run.verdict)}</span>
        <span class="repo-reviews__findings">{findingsLine(props.run.findings_kept)}</span>
      </div>

      <ReviewBrainBadge brain={props.run.brain} provenance={props.run.provenance} />

      <Show
        when={props.run.run_id}
        fallback={<span class="repo-reviews__queued">{t('repo_detail.reviews_not_started')}</span>}
      >
        {(runId) => (
          <A class={buttonClass({ variant: 'link' })} href={orgHref(`/fleet/runs/${runId()}`)}>
            {t('repo_detail.reviews_view')}
          </A>
        )}
      </Show>
    </li>
  );
}
